"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { canLinkChildren, isAdmin, isParentRole, ROLE_LABEL, type Role } from "@/lib/roles";
import type { MeResponse } from "@/lib/types";
import { PhoneEditor } from "@/components/phone-fields";
import { SmoothReveal } from "@/components/smooth-reveal";
import { StatusScreen } from "@/components/status-screen";
import { useMeActions } from "@/components/me-provider";
import { useLiveReload } from "@/components/live-provider";

type Person = {
  id: string;
  name: string;
  username: string | null;
  telegramId: string;
  phone: string;
  role: Role;
  children: Array<{ id: string; name: string }>;
};

type ChildOption = { id: string; name: string };

const assignableRoles: Role[] = ["parent", "committee", "teacher", "admin"];

export function PeopleBoard({ me, active = true }: { me: MeResponse; active?: boolean }) {
  const { refreshMe } = useMeActions();
  const [people, setPeople] = useState<Person[]>([]);
  const [children, setChildren] = useState<ChildOption[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!isAdmin(me.user.role)) {
      setLoading(false);
      return;
    }
    const data = await api<{ people: Person[]; children: ChildOption[] }>("/api/people");
    setPeople(data.people);
    setChildren(data.children);
    setLoading(false);
    void refreshMe();
  }

  useEffect(() => {
    if (!isAdmin(me.user.role)) {
      setLoading(false);
      return;
    }
    void load().catch((error) => {
      toast.error(error.message);
      setLoading(false);
    });
  }, [me.user.role]);

  useLiveReload("people", load, active);

  const grouped = useMemo(() => {
    return {
      pending: people.filter((person) => person.role === "pending"),
      parent: people.filter((person) => person.role === "parent"),
      committee: people.filter((person) => person.role === "committee"),
      teacher: people.filter((person) => person.role === "teacher"),
      admin: people.filter((person) => person.role === "admin"),
    };
  }, [people]);

  async function savePersonPhone(id: string, next: string) {
    try {
      await api("/api/people", {
        method: "PATCH",
        body: JSON.stringify({ id, phone: next }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти телефон.");
    }
  }

  async function setPersonRole(id: string, next: Role) {
    try {
      await api("/api/people", {
        method: "PATCH",
        body: JSON.stringify({ id, role: next }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося змінити роль.");
    }
  }

  async function confirmPending(id: string, next: Role, nextChildIds: string[]) {
    if (isParentRole(next) && nextChildIds.length === 0) {
      toast.error("Оберіть роль і хоча б одну дитину.");
      return;
    }
    try {
      await api("/api/people", {
        method: "PATCH",
        body: JSON.stringify({
          id,
          role: next,
          childIds: canLinkChildren(next) ? nextChildIds : [],
        }),
      });
      toast.success("Підтверджено.");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося підтвердити.");
    }
  }

  async function toggleChildLink(userId: string, childId: string, selected: boolean) {
    try {
      await api("/api/people/link", {
        method: "POST",
        body: JSON.stringify({
          userId,
          childId,
          action: selected ? "unlink" : "link",
        }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося змінити прив’язку.");
    }
  }

  async function previewAs(telegramId: string) {
    try {
      await api("/api/impersonate", {
        method: "POST",
        body: JSON.stringify({ telegramId }),
      });
      window.location.assign("/");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося відкрити перегляд.");
    }
  }

  async function removePerson(id: string) {
    try {
      await api("/api/people", {
        method: "DELETE",
        body: JSON.stringify({ id }),
      });
      toast.success("Прибрано.");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося прибрати.");
    }
  }

  if (!isAdmin(me.user.role)) {
    return <StatusScreen title="Ролі призначає адміністратор" />;
  }

  if (loading) {
    return <StatusScreen loading title="Люди класу" />;
  }

  return (
    <div className="space-y-5">
      <p className="text-sm leading-6 text-muted-foreground">
        Люди з’являються, коли пишуть боту /start. Підтвердіть роль і дітей. Натисніть ім’я — відкриється
        чат у Telegram. «Відкрити як ця людина» — лише перевірка екрана.
      </p>

      <Group title="Чекають підтвердження" empty="Ніхто не чекає.">
        {grouped.pending.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            childOptions={children}
            onRole={setPersonRole}
            onPhone={savePersonPhone}
            onConfirmPending={confirmPending}
            onToggleChild={toggleChildLink}
            onPreview={previewAs}
            onRemove={removePerson}
          />
        ))}
      </Group>
      <Group title="Батьки" empty="Ще немає батьків.">
        {grouped.parent.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            childOptions={children}
            onRole={setPersonRole}
            onPhone={savePersonPhone}
            onConfirmPending={confirmPending}
            onToggleChild={toggleChildLink}
            onPreview={previewAs}
            onRemove={removePerson}
          />
        ))}
      </Group>
      <Group title="Голова комітету" empty="">
        {grouped.committee.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            childOptions={children}
            onRole={setPersonRole}
            onPhone={savePersonPhone}
            onConfirmPending={confirmPending}
            onToggleChild={toggleChildLink}
            onPreview={previewAs}
            onRemove={removePerson}
          />
        ))}
      </Group>
      <Group title="Класний керівник" empty="">
        {grouped.teacher.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            childOptions={children}
            onRole={setPersonRole}
            onPhone={savePersonPhone}
            onConfirmPending={confirmPending}
            onToggleChild={toggleChildLink}
            onPreview={previewAs}
            onRemove={removePerson}
          />
        ))}
      </Group>
      <Group title="Адміністратор" empty="">
        {grouped.admin.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            childOptions={children}
            onRole={setPersonRole}
            onPhone={savePersonPhone}
            onConfirmPending={confirmPending}
            onToggleChild={toggleChildLink}
            onPreview={previewAs}
            onRemove={removePerson}
            hidePreview={person.id === me.user.id}
          />
        ))}
      </Group>
    </div>
  );
}

function toggleId(setter: (value: string[] | ((prev: string[]) => string[])) => void) {
  return (childId: string) => {
    setter((prev) => (prev.includes(childId) ? prev.filter((id) => id !== childId) : [...prev, childId]));
  };
}

function Group({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const has = items.filter(Boolean).length > 0 && !(Array.isArray(children) && children.length === 0);
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium">{title}</h2>
      {has ? <div className="wide-cards">{children}</div> : empty ? <p className="text-xs text-muted-foreground">{empty}</p> : null}
    </section>
  );
}

function RoleSelect({
  value,
  onChange,
}: {
  value: Role;
  onChange: (role: Role) => void;
}) {
  return (
    <select
      className="h-9 w-full rounded-xl border border-input bg-background px-2 text-sm"
      value={value}
      onChange={(event) => onChange(event.target.value as Role)}
    >
      {assignableRoles.map((item) => (
        <option key={item} value={item}>
          {ROLE_LABEL[item]}
        </option>
      ))}
    </select>
  );
}

function ChildPicker({
  options,
  selectedIds,
  onToggle,
}: {
  options: ChildOption[];
  selectedIds: string[];
  onToggle: (childId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((child) => {
        const selected = selectedIds.includes(child.id);
        return (
          <button
            key={child.id}
            type="button"
            onClick={() => onToggle(child.id)}
            className={
              selected
                ? "rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                : "rounded-full bg-accent px-3 py-1.5 text-xs font-medium"
            }
          >
            {child.name}
          </button>
        );
      })}
    </div>
  );
}

function PersonCard({
  person,
  childOptions,
  hidePreview,
  onRole,
  onPhone,
  onConfirmPending,
  onToggleChild,
  onPreview,
  onRemove,
}: {
  person: Person;
  childOptions: ChildOption[];
  hidePreview?: boolean;
  onRole: (id: string, role: Role) => void;
  onPhone: (id: string, phone: string) => Promise<void>;
  onConfirmPending: (id: string, role: Role, childIds: string[]) => Promise<void>;
  onToggleChild: (userId: string, childId: string, selected: boolean) => Promise<void>;
  onPreview: (telegramId: string) => void;
  onRemove: (id: string) => Promise<void>;
}) {
  const pending = person.role === "pending";
  const [draftRole, setDraftRole] = useState<Role>(pending ? "parent" : person.role);
  const [draftChildIds, setDraftChildIds] = useState<string[]>(() => person.children.map((child) => child.id));
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const linkedIds = person.children.map((child) => child.id);
  const showKids = pending ? canLinkChildren(draftRole) : canLinkChildren(person.role);

  async function confirm() {
    setSaving(true);
    try {
      await onConfirmPending(person.id, draftRole, draftChildIds);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    setSaving(true);
    try {
      await onRemove(person.id);
    } finally {
      setSaving(false);
      setConfirmRemove(false);
    }
  }

  return (
    <article className="rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8">
      <div>
        <p className="font-medium">{person.name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {person.username ? `@${person.username}` : person.telegramId}
        </p>
        <div className="mt-3">
          <PhoneEditor value={person.phone} onCommit={(next) => onPhone(person.id, next)} />
        </div>
        {!pending && canLinkChildren(person.role) && person.children.length > 0 ? (
          <p className="mt-1 text-xs leading-5">{person.children.map((child) => child.name).join(", ")}</p>
        ) : null}
      </div>

      {pending ? (
        <div className="mt-3 space-y-3">
          <RoleSelect value={draftRole} onChange={setDraftRole} />
          <SmoothReveal open={showKids}>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Оберіть одну або кількох дітей.</p>
              <ChildPicker options={childOptions} selectedIds={draftChildIds} onToggle={toggleId(setDraftChildIds)} />
            </div>
          </SmoothReveal>
          <Button className="w-full rounded-2xl" disabled={saving} onClick={() => void confirm()}>
            {saving ? "Зберігаємо…" : "Підтвердити"}
          </Button>
          {!hidePreview ? (
            <button
              type="button"
              className="w-full text-center text-xs font-medium text-primary"
              onClick={() => onPreview(person.telegramId)}
            >
              Відкрити як ця людина
            </button>
          ) : null}
          {!hidePreview ? (
            <button
              type="button"
              disabled={saving}
              className="w-full text-center text-xs font-medium text-destructive"
              onClick={() => void remove()}
            >
              {confirmRemove ? "Точно прибрати?" : "Прибрати"}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="h-8 rounded-lg border border-input bg-background px-2 text-xs"
              value={person.role}
              onChange={(event) => onRole(person.id, event.target.value as Role)}
            >
              {assignableRoles.map((item) => (
                <option key={item} value={item}>
                  {ROLE_LABEL[item]}
                </option>
              ))}
            </select>
            {!hidePreview ? (
              <button
                type="button"
                className="text-xs font-medium text-primary"
                onClick={() => onPreview(person.telegramId)}
              >
                Відкрити як ця людина
              </button>
            ) : null}
            {!hidePreview ? (
              <button
                type="button"
                disabled={saving}
                className="text-xs font-medium text-destructive"
                onClick={() => void remove()}
              >
                {confirmRemove ? "Точно прибрати?" : "Прибрати"}
              </button>
            ) : null}
          </div>
          <SmoothReveal open={showKids}>
            <ChildPicker
              options={childOptions}
              selectedIds={linkedIds}
              onToggle={(childId) => void onToggleChild(person.id, childId, linkedIds.includes(childId))}
            />
          </SmoothReveal>
        </div>
      )}
    </article>
  );
}
